import React, { useState, useMemo, useEffect } from 'react';
import { useApp, ROLES } from '../../context/AppContext';
import { DataTable } from '../../components/shared/DataTable';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { getDownlineUserIds } from '../../data/mockData';
import { fetchUsers, fetchPenalties, issuePenalty } from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card } from '../../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  IndianRupee,
  Scale,
  Sparkles,
  Sliders,
  Check,
  X,
  User,
  Plus
} from 'lucide-react';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';

export default function PenaltyManagement() {
  const { currentRole, currentUserId, penaltiesList, setPenaltiesList } = useApp();
  const [activeTab, setActiveTab] = useState('logs');
  const [users, setUsers] = useState([]);

  useEffect(() => {
    let isMounted = true;
    async function loadData() {
      try {
        const [usrRes, penRes] = await Promise.all([
          fetchUsers().catch(() => []),
          fetchPenalties().catch(() => [])
        ]);
        if (isMounted) {
          if (Array.isArray(usrRes)) setUsers(usrRes);
          if (Array.isArray(penRes)) setPenaltiesList(penRes);
        }
      } catch (err) {
        console.error('Failed to load penalties/users:', err);
      }
    }
    loadData();
    return () => { isMounted = false; };
  }, [setPenaltiesList]);

  // Manual Penalty Creator form states
  const [manualPenaltyUserType, setManualPenaltyUserType] = useState('field_executive'); // field_executive or vendor
  
  const filteredUsersForManualPenalty = useMemo(() => {
    return users.filter(u => u.role === manualPenaltyUserType);
  }, [users, manualPenaltyUserType]);

  const [selectedUserId, setSelectedUserId] = useState('');
  const [penaltyCaseRef, setPenaltyCaseRef] = useState('');
  const [penaltyAmount, setPenaltyAmount] = useState('');
  const [penaltyReason, setPenaltyReason] = useState('');

  // Waiver request states
  const [showWaiverRequestModal, setShowWaiverRequestModal] = useState(false);
  const [selectedPenaltyForWaiver, setSelectedPenaltyForWaiver] = useState(null);
  const [waiverJustificationText, setWaiverJustificationText] = useState('');

  // Interactive local state for auto penalty fee rules
  const [delay1DayFee, setDelay1DayFee] = useState(50);
  const [delay2DayFee, setDelay2DayFee] = useState(100);
  const [qcRejectFee, setQcRejectFee] = useState(200);
  const [reassignFee, setReassignFee] = useState(150);
  const [overdueFee, setOverdueFee] = useState(300);

  // Sync selectedUserId when manualPenaltyUserType changes
  const handleUserTypeChange = (val) => {
    setManualPenaltyUserType(val);
    const matchedUsers = users.filter(u => u.role === val);
    setSelectedUserId(matchedUsers[0]?.id || '');
  };

  // Dynamically map global AppContext penaltiesList to view objects and apply RBAC filters
  const penalties = useMemo(() => {
    let filteredList = penaltiesList;
    if (currentRole === ROLES.FIELD_EXECUTIVE) {
      filteredList = penaltiesList.filter(p => p.userId === currentUserId);
    } else if (currentRole === ROLES.VENDOR) {
      const downlines = getDownlineUserIds(currentUserId);
      filteredList = penaltiesList.filter(p => p.userId === currentUserId || downlines.includes(p.userId));
    }

    return filteredList.map(p => {
      const userObj = users.find(u => u.id === p.userId) || { name: 'User' };
      return {
        ...p,
        user: userObj,
        refNumber: p.caseId,
        status: p.status === 'Deducted' ? 'applied' : 
                p.status === 'Waive Off Requested' ? 'waiver_requested' : 
                p.status === 'Waived' ? 'waived' : 'waiver_rejected'
      };
    });
  }, [penaltiesList, currentRole, currentUserId, users]);

  // Handle Manual Penalty creation (FE & Vendor Wise)
  const handleCreatePenalty = (e) => {
    e.preventDefault();
    if (!penaltyAmount || Number(penaltyAmount) <= 0 || !penaltyReason) {
      toast.error('Please enter a valid amount and deduction justification.');
      return;
    }
    const targetUser = users.find(u => u.id === selectedUserId);
    if (!targetUser) {
      toast.error('Selected user could not be found.');
      return;
    }

    const newPenalty = {
      id: `PEN-${Date.now().toString().slice(-4)}`,
      caseId: penaltyCaseRef || 'CASE-N/A',
      userId: selectedUserId,
      reason: penaltyReason,
      amount: Number(penaltyAmount),
      status: 'Deducted',
      date: new Date().toISOString().split('T')[0]
    };

    setPenaltiesList(prev => [newPenalty, ...prev]);
    issuePenalty(newPenalty).catch(err => console.warn('Failed to issue penalty on backend:', err));
    toast.success(`Deduction of ₹${penaltyAmount} applied to ${targetUser.name} (${targetUser.role === 'vendor' ? 'Vendor' : 'FE'})!`);
    
    // Reset form
    setPenaltyAmount('');
    setPenaltyReason('');
    setPenaltyCaseRef('');
  };

  // Submit Waiver Request (Manager, Sup Manager, Admin)
  const handleRaiseWaiver = (id, justification) => {
    setPenaltiesList(prev => prev.map(p => {
      if (p.id === id) {
        return {
          ...p,
          status: 'Waive Off Requested',
          waiverJustification: justification,
          requestedBy: currentRole
        };
      }
      return p;
    }));
    toast.success('Waiver request submitted on behalf of account successfully!');
  };

  const handleRaiseWaiverRequestSubmit = () => {
    if (!waiverJustificationText) {
      toast.error('Please enter a waiver justification.');
      return;
    }
    handleRaiseWaiver(selectedPenaltyForWaiver.id, waiverJustificationText);
    setShowWaiverRequestModal(false);
    setWaiverJustificationText('');
  };

  // Approve / Reject Waiver (Admin & Supreme Manager Only)
  const handleWaiverAction = (id, actionType) => {
    setPenaltiesList(prev => prev.map(p => {
      if (p.id === id) {
        return {
          ...p,
          status: actionType === 'approve' ? 'Waived' : 'Deducted'
        };
      }
      return p;
    }));
    
    if (actionType === 'approve') {
      toast.success('Penalty waived off successfully!');
    } else {
      toast.error('Waiver request rejected successfully!');
    }
  };

  const handleSaveRules = () => {
    toast.success('Auto penalty calculation rules updated and saved!');
  };

  // Filter lists based on dynamic states
  const pendingWaivers = penalties.filter(p => p.status === 'waiver_requested');
  const appliedPenalties = penalties.filter(p => ['applied', 'waiver_rejected'].includes(p.status));
  const waivedPenalties = penalties.filter(p => p.status === 'waived');
  const rejectedWaivers = penalties.filter(p => p.status === 'waiver_rejected');

  const columns = [
    {
      key: 'refNumber',
      header: 'Case Ref',
      render: (row) => <span className="font-mono text-xs font-semibold text-slate-700">{row.refNumber || 'CASE-002'}</span>
    },
    {
      key: 'user',
      header: 'FE / Vendor Name',
      render: (row) => (
        <div className="flex items-center gap-3">
          <Avatar className="w-8 h-8">
            <AvatarImage src={row.user.avatar} />
            <AvatarFallback>{row.user.name.charAt(0)}</AvatarFallback>
          </Avatar>
          <div>
            <p className="text-xs font-semibold text-slate-800">{row.user.name}</p>
            <p className="text-[10px] text-slate-500 capitalize">{row.user.role.replace('_', ' ')}</p>
          </div>
        </div>
      )
    },
    {
      key: 'reason',
      header: 'Breach / Penalty Reason',
      render: (row) => (
        <div className="space-y-0.5">
          <p className="text-xs text-slate-600 font-medium">{row.reason}</p>
          {row.waiverJustification && (
            <p className="text-[10px] text-slate-400 italic">Justification: "{row.waiverJustification}"</p>
          )}
        </div>
      )
    },
    {
      key: 'amount',
      header: 'Amount',
      sortable: true,
      render: (row) => <span className="text-xs font-bold text-rose-600">₹{row.amount}</span>
    },
    {
      key: 'date',
      header: 'Accrued Date',
      sortable: true,
      render: (row) => (
        <span className="text-xs text-slate-500">
          {format(new Date(row.date), 'MMM dd, yyyy')}
        </span>
      )
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <StatusBadge
          status={row.status === 'applied' ? 'danger' : row.status === 'waived' ? 'success' : row.status === 'waiver_rejected' ? 'danger' : 'warning'}
          label={row.status === 'applied' ? 'Deducted' : row.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
        />
      )
    },
    {
      key: 'actions',
      header: 'Waiver Actions',
      render: (row) => {
        // Status: pending waive review
        if (row.status === 'waiver_requested') {
          if ([ROLES.ADMIN, ROLES.SUPREME_MANAGER].includes(currentRole)) {
            return (
              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleWaiverAction(row.id, 'approve')}
                  className="text-emerald-700 border-emerald-200 bg-emerald-50 hover:bg-emerald-100 h-7 text-[10px] px-2.5 font-semibold rounded-lg"
                >
                  <Check className="w-3 h-3 mr-1" />
                  Approve
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleWaiverAction(row.id, 'reject')}
                  className="text-rose-700 border-rose-200 bg-rose-50 hover:bg-rose-100 h-7 text-[10px] px-2.5 font-semibold rounded-lg"
                >
                  <X className="w-3 h-3 mr-1" />
                  Reject
                </Button>
              </div>
            );
          } else {
            return <span className="text-[10px] text-amber-600 bg-amber-50 px-2 py-0.5 rounded font-semibold border border-amber-100">Pending Waiver Review</span>;
          }
        }
        
        // Status: active deduction, allow managers/admins/FEs/Vendors to raise waiver request
        if (row.status === 'applied') {
          if ([ROLES.ADMIN, ROLES.SUPREME_MANAGER, ROLES.MANAGER, ROLES.FIELD_EXECUTIVE, ROLES.VENDOR].includes(currentRole)) {
            return (
              <div onClick={(e) => e.stopPropagation()}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedPenaltyForWaiver(row);
                    setShowWaiverRequestModal(true);
                  }}
                  className="text-blue-700 border-blue-200 bg-blue-50 hover:bg-blue-100 h-7 text-[10px] px-2.5 font-semibold rounded-lg"
                >
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  Request Waiver
                </Button>
              </div>
            );
          }
        }

        if (row.status === 'waived') {
          return <span className="text-[10px] text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded font-semibold border border-emerald-100">Waived Off</span>;
        }

        return <span className="text-[10px] text-slate-400 font-medium">Auto Locked</span>;
      }
    }
  ];

  const stats = [
    { label: 'Accrued Deductions', value: appliedPenalties.length, icon: AlertTriangle, color: 'text-rose-600', bg: 'bg-rose-50 border-rose-100' },
    { label: 'Pending Waiver Requests', value: pendingWaivers.length, icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-100' },
    { label: 'Approved Waivers', value: waivedPenalties.length, icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-100' },
    { label: 'Total Penalty Deductions', value: `₹${appliedPenalties.reduce((sum, p) => sum + p.amount, 0)}`, icon: IndianRupee, color: 'text-rose-600', bg: 'bg-rose-50 border-rose-100' }
  ];

  return (
    <div className="p-6 space-y-6 text-slate-800 min-h-screen bg-slate-50 animate-fade-in" data-testid="penalty-management-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">
            Deduction & Penalty Management
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            Set manual penalties FE & Vendor wise, raise waive-off justification requests, and review pending waivers.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {[ROLES.ADMIN, ROLES.SUPREME_MANAGER, ROLES.MANAGER].includes(currentRole) && (
            <Button 
              onClick={() => setActiveTab('manual_set')}
              className="bg-rose-600 hover:bg-rose-700 text-white font-semibold text-xs py-2 px-4 rounded-xl gap-2 shadow-sm h-auto"
            >
              <Plus className="w-4 h-4" />
              Set Manual Penalty
            </Button>
          )}
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {stats.map((stat, idx) => {
          const Icon = stat.icon;
          return (
            <Card key={idx} className={`crm-card p-4 border rounded-2xl ${stat.bg}`}>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-white border border-slate-100 shadow-xs">
                  <Icon className={`w-5 h-5 ${stat.color}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold font-mono text-slate-900 leading-tight">{stat.value}</p>
                  <p className="text-[11px] text-slate-500 font-medium mt-1">{stat.label}</p>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Main Tabs Container */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-white border border-slate-200 p-1.5 flex flex-wrap h-auto gap-1 rounded-2xl shadow-xs">
          <TabsTrigger value="logs" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 text-slate-600 text-xs font-semibold py-2 px-4 rounded-xl transition-all">
            Deduction Logs ({penalties.length})
          </TabsTrigger>
          {/* Set Manual Penalty: Admin, Supreme Manager, Manager */}
          {[ROLES.ADMIN, ROLES.SUPREME_MANAGER, ROLES.MANAGER].includes(currentRole) && (
            <TabsTrigger value="manual_set" className="data-[state=active]:bg-rose-50 data-[state=active]:text-rose-600 text-slate-600 text-xs font-semibold py-2 px-4 rounded-xl transition-all">
              Set Manual Penalty
            </TabsTrigger>
          )}
          {/* Configure Auto Rules: Admin and Supreme Manager only */}
          {[ROLES.ADMIN, ROLES.SUPREME_MANAGER].includes(currentRole) && (
            <TabsTrigger value="rules" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 text-slate-600 text-xs font-semibold py-2 px-4 rounded-xl transition-all">
              Configure Auto Rules
            </TabsTrigger>
          )}
          {/* Waive Requests: Admin, Supreme Manager, Manager can view and act */}
          {[ROLES.ADMIN, ROLES.SUPREME_MANAGER, ROLES.MANAGER].includes(currentRole) && (
            <TabsTrigger value="waiver" className="data-[state=active]:bg-amber-50 data-[state=active]:text-amber-600 text-slate-600 text-xs font-semibold py-2 px-4 rounded-xl transition-all">
              Waive Requests ({pendingWaivers.length})
            </TabsTrigger>
          )}
          {/* Approved Waivers: Admin and Supreme Manager only */}
          {[ROLES.ADMIN, ROLES.SUPREME_MANAGER].includes(currentRole) && (
            <TabsTrigger value="approved" className="data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-600 text-slate-600 text-xs font-semibold py-2 px-4 rounded-xl transition-all">
              Approved Waivers ({waivedPenalties.length})
            </TabsTrigger>
          )}
        </TabsList>

        {/* Tab 1: Penalty Logs */}
        <TabsContent value="logs" className="mt-6">
          <DataTable
            columns={columns}
            data={penalties}
            searchPlaceholder="Search deduction records..."
            selectable={false}
          />
        </TabsContent>

        {/* Tab 2: Set Manual Penalty FE & Vendor Wise */}
        <TabsContent value="manual_set" className="mt-6">
          <Card className="crm-card border-slate-200 bg-white p-6 space-y-6 rounded-3xl shadow-sm">
            <div className="flex items-center gap-3 pb-3 border-b border-slate-200">
              <User className="w-5 h-5 text-rose-500" />
              <div>
                <h3 className="text-base font-semibold text-slate-800">Set Penalty FE & Vendor Wise</h3>
                <p className="text-xs text-slate-500">Apply a custom penalty deduction record to a specific Field Executive or Vendor account.</p>
              </div>
            </div>

            <form onSubmit={handleCreatePenalty} className="space-y-5 pt-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                
                {/* Select User Type */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold text-slate-700">Account Type</Label>
                  <select
                    value={manualPenaltyUserType}
                    onChange={(e) => handleUserTypeChange(e.target.value)}
                    className="w-full rounded-xl bg-white border border-slate-200 px-3 py-2.5 text-xs font-semibold text-slate-800 outline-none cursor-pointer focus:ring-2 focus:ring-rose-500/25"
                  >
                    <option value="field_executive">Field Executive (FE)</option>
                    <option value="vendor">Vendor</option>
                  </select>
                </div>

                {/* Select Specific User */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold text-slate-700">Select Target User / Entity</Label>
                  <select
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    className="w-full rounded-xl bg-white border border-slate-200 px-3 py-2.5 text-xs font-semibold text-slate-800 outline-none cursor-pointer focus:ring-2 focus:ring-rose-500/25"
                  >
                    {filteredUsersForManualPenalty.map(u => (
                      <option key={u.id} value={u.id}>{u.name} ({u.id})</option>
                    ))}
                  </select>
                </div>

                {/* Case Reference */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold text-slate-700">Associated Case Reference</Label>
                  <Input
                    type="text"
                    placeholder="e.g. CASE-004"
                    value={penaltyCaseRef}
                    onChange={(e) => setPenaltyCaseRef(e.target.value)}
                    className="rounded-xl border-slate-200 text-xs bg-slate-50/50"
                  />
                </div>

                {/* Penalty Amount */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold text-slate-700">Penalty Amount (₹)</Label>
                  <Input
                    type="number"
                    placeholder="e.g. 250"
                    value={penaltyAmount}
                    onChange={(e) => setPenaltyAmount(e.target.value)}
                    className="rounded-xl border-slate-200 text-xs bg-slate-50/50"
                  />
                </div>

              </div>

              {/* Deduction Reason */}
              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-700">Breach Reason / Deduction Details</Label>
                <textarea
                  placeholder="Enter detailed reason for the penalty deduction..."
                  value={penaltyReason}
                  onChange={(e) => setPenaltyReason(e.target.value)}
                  className="w-full rounded-2xl border border-slate-200 p-3 text-xs bg-slate-50/50 focus:outline-none focus:ring-2 focus:ring-rose-500/25 min-h-[90px]"
                />
              </div>

              <div className="flex justify-end pt-3 border-t border-slate-200">
                <Button type="submit" className="bg-rose-600 hover:bg-rose-700 text-white font-bold px-6 py-2.5 rounded-xl text-xs h-auto shadow-sm">
                  Apply Penalty Deduction
                </Button>
              </div>
            </form>
          </Card>
        </TabsContent>

        {/* Tab 3: Auto Penalty Rules (SLIDERS & INPUTS) */}
        <TabsContent value="rules" className="mt-6">
          <Card className="crm-card border-slate-200 bg-white p-6 space-y-6 rounded-3xl shadow-sm">
            <div className="flex items-center gap-3 pb-3 border-b border-slate-200">
              <Scale className="w-5 h-5 text-rose-500" />
              <div>
                <h3 className="text-base font-semibold text-slate-800">SLA Delay & Reject Rule Configurations</h3>
                <p className="text-xs text-slate-500">Fine deductions are automatically applied upon status breaches.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
              
              {/* Slidables Left Block */}
              <div className="space-y-6">
                
                {/* 1 Day Delay slider */}
                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-3">
                  <div className="flex justify-between items-center">
                    <Label className="text-xs font-semibold text-slate-700">1 Day SLA Submission Delay</Label>
                    <span className="text-xs font-mono font-bold text-rose-605 bg-rose-50 px-2 py-0.5 rounded border border-rose-100">
                      ₹{delay1DayFee}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="10"
                    max="500"
                    step="5"
                    value={delay1DayFee}
                    onChange={(e) => setDelay1DayFee(parseInt(e.target.value))}
                    className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-rose-500"
                  />
                  <div className="flex justify-between text-[10px] text-slate-500 font-semibold font-mono">
                    <span>₹10</span>
                    <span>₹250</span>
                    <span>₹500</span>
                  </div>
                </div>

                {/* 2 Day Delay slider */}
                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-3">
                  <div className="flex justify-between items-center">
                    <Label className="text-xs font-semibold text-slate-700">2+ Days SLA Submission Delay</Label>
                    <span className="text-xs font-mono font-bold text-rose-605 bg-rose-50 px-2 py-0.5 rounded border border-rose-100">
                      ₹{delay2DayFee}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="50"
                    max="1000"
                    step="10"
                    value={delay2DayFee}
                    onChange={(e) => setDelay2DayFee(parseInt(e.target.value))}
                    className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-rose-500"
                  />
                  <div className="flex justify-between text-[10px] text-slate-500 font-semibold font-mono">
                    <span>₹50</span>
                    <span>₹500</span>
                    <span>₹1000</span>
                  </div>
                </div>

                {/* QC Reject penalty slider */}
                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-3">
                  <div className="flex justify-between items-center">
                    <Label className="text-xs font-semibold text-slate-700">QC Reject / Revisit Deduction</Label>
                    <span className="text-xs font-mono font-bold text-rose-605 bg-rose-50 px-2 py-0.5 rounded border border-rose-100">
                      ₹{qcRejectFee}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="50"
                    max="500"
                    step="10"
                    value={qcRejectFee}
                    onChange={(e) => setQcRejectFee(parseInt(e.target.value))}
                    className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-rose-500"
                  />
                  <div className="flex justify-between text-[10px] text-slate-500 font-semibold font-mono">
                    <span>₹50</span>
                    <span>₹275</span>
                    <span>₹500</span>
                  </div>
                </div>

              </div>

              {/* Slidables Right Block */}
              <div className="space-y-6">
                
                {/* Case Reassignment Penalty */}
                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-3">
                  <div className="flex justify-between items-center">
                    <Label className="text-xs font-semibold text-slate-700">Case Reassignment Penalty</Label>
                    <span className="text-xs font-mono font-bold text-rose-605 bg-rose-50 px-2 py-0.5 rounded border border-rose-100">
                      ₹{reassignFee}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="20"
                    max="400"
                    step="5"
                    value={reassignFee}
                    onChange={(e) => setReassignFee(parseInt(e.target.value))}
                    className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-rose-500"
                  />
                  <div className="flex justify-between text-[10px] text-slate-500 font-semibold font-mono">
                    <span>₹20</span>
                    <span>₹210</span>
                    <span>₹400</span>
                  </div>
                </div>

                {/* Overdue Penalty */}
                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-3">
                  <div className="flex justify-between items-center">
                    <Label className="text-xs font-semibold text-slate-700">Case Overdue Breach Penalty</Label>
                    <span className="text-xs font-mono font-bold text-rose-650 bg-rose-50 px-2 py-0.5 rounded border border-rose-100">
                      ₹{overdueFee}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="100"
                    max="1000"
                    step="20"
                    value={overdueFee}
                    onChange={(e) => setOverdueFee(parseInt(e.target.value))}
                    className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-rose-500"
                  />
                  <div className="flex justify-between text-[10px] text-slate-500 font-semibold font-mono">
                    <span>₹100</span>
                    <span>₹550</span>
                    <span>₹1000</span>
                  </div>
                </div>

                {/* Automation Summary Card */}
                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 flex items-center justify-between">
                  <div className="space-y-1">
                    <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5 text-amber-500 animate-spin" />
                      Active Rules Engine
                    </span>
                    <p className="text-[10px] text-slate-500 max-w-[200px] leading-relaxed">
                      SLA breaches auto-flag, calculate penalties based on these rules, and subtract from payouts.
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="text-[10px] text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded uppercase font-bold tracking-wider">
                      Operational
                    </span>
                  </div>
                </div>

              </div>

            </div>

            <div className="flex justify-end pt-3 border-t border-slate-200">
              <Button onClick={handleSaveRules} className="bg-rose-600 hover:bg-rose-700 text-white font-semibold px-6 py-2.5 rounded-xl text-xs h-auto shadow-sm">
                Save Auto Rules
              </Button>
            </div>
          </Card>
        </TabsContent>

        {/* Tab 4: Waiver Requests */}
        <TabsContent value="waiver" className="mt-6">
          <DataTable
            columns={columns}
            data={pendingWaivers}
            searchPlaceholder="Search pending waivers..."
            selectable={false}
          />
        </TabsContent>

        {/* Tab 5: Approved Waivers */}
        <TabsContent value="approved" className="mt-6">
          <DataTable
            columns={columns}
            data={waivedPenalties}
            searchPlaceholder="Search approved waivers..."
            selectable={false}
          />
        </TabsContent>

      </Tabs>

      {/* WAIVER REQUEST MODAL */}
      <Dialog open={showWaiverRequestModal} onOpenChange={setShowWaiverRequestModal}>
        <DialogContent className="bg-white border-slate-200 max-w-md text-slate-800 rounded-3xl shadow-2xl">
          <DialogHeader>
            <DialogTitle className="text-slate-900 text-lg font-bold flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-rose-500 animate-pulse" />
              Request Penalty Waiver
            </DialogTitle>
          </DialogHeader>

          <div className="py-3 space-y-4">
            <p className="text-xs text-slate-500 leading-relaxed">
              Submit a wave-off justification request on behalf of the selected account. Active administrators or managers will review this request.
            </p>

            {selectedPenaltyForWaiver && (
              <div className="bg-slate-50 p-4 border border-slate-150 rounded-2xl text-xs space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-500">Account Holder:</span>
                  <span className="font-bold text-slate-805">{selectedPenaltyForWaiver.user?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Case ID:</span>
                  <span className="font-mono font-bold text-blue-600">{selectedPenaltyForWaiver.refNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Accrued Penalty:</span>
                  <span className="font-bold text-rose-600">₹{selectedPenaltyForWaiver.amount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Deduction Reason:</span>
                  <span className="font-medium text-slate-750">{selectedPenaltyForWaiver.reason}</span>
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-slate-705">Wave-Off Reason / Justification</Label>
              <textarea
                placeholder="Enter formal justification for waiving this penalty..."
                value={waiverJustificationText}
                onChange={(e) => setWaiverJustificationText(e.target.value)}
                className="w-full rounded-2xl border border-slate-200 p-3 text-xs bg-slate-50/50 focus:outline-none focus:ring-2 focus:ring-blue-500/25 min-h-[90px]"
              />
            </div>
          </div>

          <DialogFooter className="border-t border-slate-100 pt-3 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowWaiverRequestModal(false)} className="border-slate-200 text-slate-700 hover:bg-slate-50 rounded-xl text-xs px-4 py-2">
              Cancel
            </Button>
            <Button onClick={handleRaiseWaiverRequestSubmit} className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs px-4 py-2">
              Submit Waiver Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
